import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase.js';

export interface UserActivityLog {
  id?: string;
  userId: string;
  userEmail: string;
  userName?: string;
  action: string;
  details?: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
  sessionId?: string;
  page?: string;
  duration?: number; // 페이지 체류 시간 (초)
}

export interface ActivityLogFilter {
  userId?: string;
  userEmail?: string;
  userName?: string;
  action?: string;
  startDate?: Date;
  endDate?: Date;
  page?: string;
  limit?: number;
}

class UserActivityLogService {
  private collectionName = 'userActivityLogs';

  // 활동 로그 기록
  async logActivity(log: Omit<UserActivityLog, 'id' | 'timestamp'>): Promise<void> {
    try {
      const logData = {
        ...log,
        timestamp: Timestamp.fromDate(new Date()),
        userAgent: navigator.userAgent,
        sessionId: this.getSessionId()
      };

      await addDoc(collection(db, this.collectionName), logData);
      console.log('[UserActivityLogService] 활동 로그 기록 완료:', log.action);
    } catch (error) {
      console.error('[UserActivityLogService] 활동 로그 기록 실패:', error);
    }
  }

  // 모든 사용자의 활동 로그 조회 (관리자용)
  async getAllActivityLogs(filter: ActivityLogFilter = {}): Promise<UserActivityLog[]> {
    try {
      const {
        userId,
        userEmail,
        userName,
        action,
        startDate,
        endDate,
        page,
        limit: queryLimit = 100
      } = filter;

      // 기본 쿼리 구성
      let q = query(
        collection(db, this.collectionName),
        orderBy('timestamp', 'desc'),
        limit(queryLimit)
      );

      // Firestore에서 기본 필터링 (복합 인덱스 제한을 피하기 위해 최소한만 적용)
      if (userId) {
        q = query(q, where('userId', '==', userId));
      }
      if (startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      const querySnapshot = await getDocs(q);
      let logs: UserActivityLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp.toDate()
        } as UserActivityLog);
      });

      // 클라이언트 사이드에서 모든 필터링 적용 (복합 인덱스 제한 해결)
      if (userEmail) {
        logs = logs.filter(log => 
          log.userEmail && log.userEmail.toLowerCase().includes(userEmail.toLowerCase())
        );
      }
      
      if (userName) {
        logs = logs.filter(log => 
          log.userName && log.userName.toLowerCase().includes(userName.toLowerCase())
        );
      }

      if (action) {
        logs = logs.filter(log => log.action === action);
      }

      if (page) {
        logs = logs.filter(log => log.page === page);
      }

      console.log(`[UserActivityLogService] ${logs.length}개의 활동 로그 조회 완료`);
      return logs;
    } catch (error) {
      console.error('[UserActivityLogService] 활동 로그 조회 실패:', error);
      return [];
    }
  }

  // 특정 사용자의 활동 로그 조회
  async getUserActivityLogs(userId: string, limitCount: number = 50): Promise<UserActivityLog[]> {
    return this.getAllActivityLogs({ userId, limit: limitCount });
  }

  // 로그인 기록
  async logLogin(userId: string, userEmail: string, userName?: string): Promise<void> {
    await this.logActivity({
      userId,
      userEmail,
      userName,
      action: 'LOGIN',
      details: '사용자 로그인',
      page: '/login'
    });
  }

  // 로그아웃 기록
  async logLogout(userId: string, userEmail: string, userName?: string): Promise<void> {
    await this.logActivity({
      userId,
      userEmail,
      userName,
      action: 'LOGOUT',
      details: '사용자 로그아웃'
    });
  }

  // 페이지 방문 기록
  async logPageVisit(userId: string, userEmail: string, userName: string, page: string): Promise<void> {
    await this.logActivity({
      userId,
      userEmail,
      userName,
      action: 'PAGE_VISIT',
      details: `페이지 방문: ${page}`,
      page
    });
  }

  // 데이터 수정 기록
  async logDataModification(
    userId: string, 
    userEmail: string, 
    userName: string, 
    action: string, 
    details: string,
    page?: string
  ): Promise<void> {
    await this.logActivity({
      userId,
      userEmail,
      userName,
      action,
      details,
      page
    });
  }

  // 세션 ID 생성/가져오기
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  // 액션 타입별 한국어 변환
  getActionDisplayName(action: string): string {
    const actionMap: { [key: string]: string } = {
      'LOGIN': '로그인',
      'LOGOUT': '로그아웃',
      'PAGE_VISIT': '페이지 방문',
      'DATA_CREATE': '데이터 생성',
      'DATA_UPDATE': '데이터 수정',
      'DATA_DELETE': '데이터 삭제',
      'FILE_UPLOAD': '파일 업로드',
      'EXPORT_DATA': '데이터 내보내기',
      'USER_CREATE': '사용자 생성',
      'USER_UPDATE': '사용자 수정',
      'USER_DELETE': '사용자 삭제',
      'TEAM_CREATE': '팀 생성',
      'TEAM_UPDATE': '팀 수정',
      'TEAM_DELETE': '팀 삭제',
      'PAYROLL_CALCULATE': '급여 계산',
      'PERFORMANCE_REFRESH': '성과 데이터 새로고침'
    };
    return actionMap[action] || action;
  }
}

export const userActivityLogService = new UserActivityLogService();
export default userActivityLogService; 